/**
 * Словари онбординга — Block 0 Phase 2 «Экран Вход» (PRD `docs/prd/v4/02-onboarding.md` §2.2
 * + `01-foundation.md` §1.3 + §1.7) + ADR-0012.
 *
 * Используется:
 *   - UI Stage 1 (`/onboarding/lang-city`) — выбор языка + города при первом входе.
 *   - UI Stage 2 (`/onboarding/register`) — `<select>` для city/specialization при soft-gate.
 *   - Zod-схемы в `contracts/onboarding.ts` (City/Specialization/Region/Language enums).
 *   - Auto region derivation: `CITY_REGION_MAP[city] → region`. После первой регистрации
 *     `region` immutable (P-008) — клиент не разрешает мутацию через `setLangCity` после
 *     `setProfile`, серверная регистрация фиксирует регион в JWT.
 *
 * Все значения сохраняются как есть — без нормализации (P-001 spirit).
 */

// ── 20 городов = канон-20 (ADR-0085; множество совпадает с CANON_CITY_ORDER) ──
// Группировка по региону — для CITY_REGION_MAP. Написания строго как в
// CANON_CITY_ORDER (dealer-cities.ts, Блок 4). Караганда убрана (ADR-0085).
// Совпадение множества с CANON_CITY_ORDER проверяется тестом.
export const CITIES = Object.freeze([
  // KZ
  'Астана',
  'Алматы',
  // RU
  'Москва',
  'Санкт-Петербург',
  'Новосибирск',
  'Екатеринбург',
  'Краснодар',
  'Нижний Новгород',
  // RU — Подмосковье (агломерация Москвы)
  'Мытищи',
  'Балашиха',
  'Химки',
  'Подольск',
  'Люберцы',
  'Одинцово',
  'Котельники',
  'Солнечногорск',
  'Красногорск',
  'Зеленоград',
  'Щёлково',
  // UZ
  'Ташкент',
] as const);
export type City = (typeof CITIES)[number];

// ── 6 специализаций из PRD §2.2 ───────────────────────────────────────────
// Хранятся в БД как enum-значения латиницей (data-model.md §10 — `userSpecialtyEnum`).
// UI рендерит русские лейблы из `SPECIALIZATION_LABELS_RU` (см. ниже).
export const SPECIALIZATIONS = Object.freeze([
  'furniture_maker',
  'furniture_company_owner',
  'assembler',
  'designer',
  'designer_complectator',
  'foreman',
] as const);
export type Specialization = (typeof SPECIALIZATIONS)[number];

// Русские лейблы для UI (Stage 2 register). Один источник истины — здесь;
// LinguiJS-локализация на UZ/KZ — отдельная фаза.
export const SPECIALIZATION_LABELS_RU: Record<Specialization, string> = {
  furniture_maker: 'Мебельщик',
  furniture_company_owner: 'Владелец мебельной компании',
  assembler: 'Сборщик',
  designer: 'Дизайнер',
  designer_complectator: 'Дизайнер-комплектатор',
  foreman: 'Бригадир',
};

// ── 3 региона = 3 страны (PRD §1.7, P-007/P-008) ──────────────────────────
// Регион определяет, в каком региональном Postgres хранятся ПДн пользователя.
// Immutable после первой регистрации (P-008).
// Имя `UserRegion` (не просто `Region`) — `Region` уже занят в `contracts/decor-match.ts`
// для image-region (LAB photo-pick — круг/среднее по фото). Чтобы избежать collision
// в `index.ts` re-export, geographic регион называется `UserRegion`.
export const REGIONS = Object.freeze(['KZ', 'RU', 'UZ'] as const);
export type UserRegion = (typeof REGIONS)[number];

// ── 3 языка интерфейса (PRD §1.1, §1.3) ───────────────────────────────────
// `kz` = казахский (`kk` в data-model.md §10 `userLanguageEnum` — расхождение
// сериализации; в Phase 2 контракт использует `kz` как менее технический;
// миграция на ISO-639 `kk` — отдельная задача backend Phase 9+).
export const LANGUAGES = Object.freeze(['ru', 'kz', 'uz'] as const);
export type Language = (typeof LANGUAGES)[number];

// ── Auto region derivation (PRD §1.7 + ADR-0012 §4) ───────────────────────
// Клиентская копия mapping'а; в Phase 9+ серверный source of truth (по этой
// же таблице) валидирует регистрацию. Расхождение между клиентом и сервером
// = bug, ловится integration-тестом.
export const CITY_REGION_MAP: Record<City, UserRegion> = {
  // KZ
  Астана: 'KZ',
  Алматы: 'KZ',
  // RU
  Москва: 'RU',
  'Санкт-Петербург': 'RU',
  Новосибирск: 'RU',
  Екатеринбург: 'RU',
  Краснодар: 'RU',
  'Нижний Новгород': 'RU',
  // RU — Подмосковье (агломерация Москвы)
  Мытищи: 'RU',
  Балашиха: 'RU',
  Химки: 'RU',
  Подольск: 'RU',
  Люберцы: 'RU',
  Одинцово: 'RU',
  Котельники: 'RU',
  Солнечногорск: 'RU',
  Красногорск: 'RU',
  Зеленоград: 'RU',
  Щёлково: 'RU',
  // UZ
  Ташкент: 'UZ',
};
